import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaPhoneAlt, FaArrowRight } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../../../components/Hooks/axiosInstance";
import SetTitle from "../../../components/SetTitle";
import Footer from "../../../components/Footer/Footer";
import styles from "./LittleRockstar.module.scss";
import classroomImg from "./assets/about_classroom.jpg";
import activitiesChildImg from "./assets/learning_activities.jpg";
import gallerySteppingImg from "./assets/gallery_stepping.jpg";
import galleryBlocksImg from "./assets/gallery_blocks.jpg";
import galleryPaintingImg from "./assets/gallery_painting.jpg";
import galleryDanceImg from "./assets/gallery_dance.jpg";
import planVisitMapImg from "./assets/plan_visit_map.jpg";
import rockstarBandKidsImg from "./assets/dancing band.png";
import musicNotesLeftImg from "./assets/music_notes_left.png";
import musicNotesRightImg from "./assets/music_notes_right.png";

const LittleRockstar = () => {
	SetTitle("Little Rockstar Pre School | MINE kids");
	const navigate = useNavigate();

	// Section Refs for smooth scrolling
	const inquiryFormRef = useRef(null);
	const gallerySectionRef = useRef(null);
	const visitSectionRef = useRef(null);

	const scrollToGallery = () => {
		if (gallerySectionRef.current) {
			gallerySectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const scrollToVisit = () => {
		if (visitSectionRef.current) {
			visitSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const [formData, setFormData] = useState({
		parentName: "",
		phoneNumber: "",
		email: "",
		childName: "",
		childAge: "",
		message: "",
	});
	const [submitted, setSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleInquireClick = (e) => {
		if (e) e.preventDefault();
		if (inquiryFormRef.current) {
			inquiryFormRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
			const firstInput = inquiryFormRef.current.querySelector("input");
			if (firstInput) {
				setTimeout(() => {
					firstInput.focus({ preventScroll: true });
				}, 600);
			}
		}
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.parentName || !formData.phoneNumber || !formData.childName) {
			return toast.warn("Parent name, phone number, and child's name are required.");
		}

		setLoading(true);
		try {
			const { data } = await axiosInstance.post("/admission/inquiry", {
				...formData,
				schoolName: "Little Rockstar",
			});
			toast.success(data?.msg || "Inquiry submitted successfully! We will contact you soon.");
			setSubmitted(true);
			setFormData({
				parentName: "",
				phoneNumber: "",
				email: "",
				childName: "",
				childAge: "",
				message: "",
			});
			setTimeout(() => setSubmitted(false), 4000);
		} catch (err) {
			console.error(err);
			toast.error(err?.response?.data?.msg || err?.response?.data?.message || "Failed to submit inquiry. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={styles.LittleRockstar}>
			<div className={styles.Container}>
				{/* Top Hero Banner */}
				<div className={styles.HeroBanner}>
					{/* Yellow Ambient Glow behind graphics */}
					<div className={styles.HeroStarGlow}></div>

					{/* Left Content */}
					<div className={styles.HeroContent}>
						<div className={styles.WelcomeBadge}>
							<span>Welcome to</span>
						</div>

						<h1 className={styles.HeroTitle}>
							Little Rockstar<br />Pre School
						</h1>

						<div className={styles.SubTagWrapper}>
							<span className={styles.SubTag}>Pre school and creche</span>
						</div>

						<p className={styles.Description}>
							Where every child shines, learns and becomes a little rock star !
						</p>

						<div className={styles.HeroActionButtons}>
							<a href="tel:7848933200" className={styles.SolidCallBtn}>
								<FaPhoneAlt className={styles.PhoneIcon} />
								<span>78489 33200</span>
							</a>
							<a
								href="#inquiry-form"
								onClick={handleInquireClick}
								className={styles.InquireBtn}
							>
								<span>Inquire Now</span>
								<div className={styles.ArrowCircle}>
									<FaArrowRight className={styles.ArrowIcon} />
								</div>
							</a>
						</div>
					</div>

					{/* Right Illustrations & Kids */}
					<div className={styles.HeroGraphics}>
						<img src={musicNotesLeftImg} alt="Music Notes" className={styles.MusicNotesStickerLeft} />
						<img
							src={rockstarBandKidsImg}
							alt="Little Rockstar Band Kids"
							className={styles.KidsBandPhoto}
						/>
						<img src={musicNotesRightImg} alt="Music Notes" className={styles.MusicNotesStickerRight} />
					</div>
				</div>

				{/* About Us Section */}
				<section className={styles.AboutSection}>
					<div className={styles.ImageCard}>
						<img src={classroomImg} alt="Little Rockstar Classroom" className={styles.CardImg} />
					</div>

					<div className={styles.TextContent}>
						<h2 className={styles.SectionTitle}>About us</h2>
						<div className={styles.YellowUnderline}></div>
						<p className={styles.SectionDescription}>
							Little Rockstar Pre School is an inspiring and joyful learning sanctuary located in Ranasinghpur, Bhubaneswar. As part of the MINE kids family, we believe every child is born with unique talents, creative rhythm, and boundless potential. Through a harmonious blend of play, music, early academics, and exploratory activities, we cultivate curiosity, self-confidence, and a lifelong love for learning.
						</p>
					</div>
				</section>

				{/* Learning and Activities Section */}
				<section className={`${styles.AboutSection} ${styles.ReverseOnDesktop}`}>
					<div className={styles.TextContent}>
						<h2 className={styles.SectionTitle}>Learning and Activities</h2>
						<div className={`${styles.YellowUnderline} ${styles.YellowUnderlineLong}`}></div>
						<p className={styles.SectionDescription}>
							At Little Rockstar, learning is an interactive celebration. Children engage in rhythm and melody, creative arts, storytelling, expressive drama, sensory play, and foundational STEM concepts. Our curriculum inspires independent thinking, builds strong communication skills, and nurtures social-emotional development in a safe, vibrant, and loving atmosphere.
						</p>
					</div>

					<div className={styles.ImageCard}>
						<img src={activitiesChildImg} alt="Child engaged in creative learning" className={styles.CardImg} />
					</div>
				</section>

				{/* Gallery Section - Explore our space (Exact 1180px Layout) */}
				<section className={styles.GallerySection} ref={gallerySectionRef}>
					{/* 4 Overlapping Cards Collage */}
					<div className={styles.GalleryCollageWrapper}>
						<div className={styles.GalleryCollage}>
							{/* Card 1 (Top Left: Dancing on carpet) */}
							<div className={`${styles.CollageCard} ${styles.CardTopLeft}`}>
								<img src={galleryDanceImg} alt="Music, Rhythm & Dance" className={styles.CollageImg} />
							</div>

							{/* Card 2 (Middle: Hand Painting) */}
							<div className={`${styles.CollageCard} ${styles.CardMiddle}`}>
								<img src={galleryPaintingImg} alt="Creative Painting & Art" className={styles.CollageImg} />
							</div>

							{/* Card 0 (Bottom Left: Building Blocks) */}
							<div className={`${styles.CollageCard} ${styles.CardBottomLeft}`}>
								<img src={galleryBlocksImg} alt="Building Blocks & Mathematics" className={styles.CollageImg} />
							</div>

							{/* Card 3 (Bottom Right: Sensory Stepping Stones) */}
							<div className={`${styles.CollageCard} ${styles.CardBottomRight}`}>
								<img src={gallerySteppingImg} alt="Sensory & Motor Activities" className={styles.CollageImg} />
							</div>
						</div>
					</div>

					{/* Right Content */}
					<div className={styles.GalleryContent}>
						<div className={styles.GalleryTopRow}>
							<span className={styles.GallerySubtitle}>A glimpse into our space</span>
							<Link to="/Gallery" className={styles.SeeAllLink}>See all</Link>
						</div>
						<h2 className={styles.GalleryTitle}>Explore our space</h2>
						<div className={styles.GalleryYellowUnderline}></div>
						<p className={styles.GalleryDescription}>
							A glimpse into our joyful learning spaces, activities, and everyday moments.
						</p>
					</div>
				</section>

				{/* Plan Your Visit / Reach Us Section (Exact Figma Specs) */}
				<section className={styles.VisitSection} ref={visitSectionRef}>
					<div className={styles.VisitContent}>
						<span className={styles.VisitSubtitle}>How to reach us ?</span>
						<h2 className={styles.VisitTitle}>Plan your visit</h2>

						<div className={styles.TransitList}>
							{/* By Road */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9-1.8-.5-4.5-1.1-4.5-1.1s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H6c-.6 0-1.1.4-1.4.9L3.2 10.8c-.1.4-.2.8-.2 1.2v4c0 .6.4 1 1 1h2" />
										<circle cx="8" cy="17" r="2" />
										<path d="M10 17h4" />
										<circle cx="18" cy="17" r="2" />
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>By road</h4>
									<p>Easily accessible via Khandagiri–Ranasinghpur Road.</p>
								</div>
							</div>

							{/* Nearest Railway */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<rect x="4" y="3" width="16" height="15" rx="2" />
										<path d="M4 11h16" />
										<path d="M12 3v8" />
										<path d="m8 18-2.5 3.5" />
										<path d="m16 18 2.5 3.5" />
										<circle cx="8" cy="15" r="0.75" fill="white" />
										<circle cx="16" cy="15" r="0.75" fill="white" />
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>Nearest Railway station</h4>
									<p>Sarkantra Railway Station.</p>
								</div>
							</div>

							{/* Nearest Airport */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3l5.3 3.5-2 3H4.5l-1 1 3 2 2 3 1-1v-2.5l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.3c.4-.2.6-.6.5-1.1z" />
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>Nearest Airport</h4>
									<p>Biju Patnaik International Airport.</p>
								</div>
							</div>

							{/* Visiting Hours */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<circle cx="12" cy="12" r="8.5" />
										<polyline points="12 7.5 12 12 15.5 13.5" />
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>Visiting hours</h4>
									<p>8:30 AM – 5:30 PM</p>
								</div>
							</div>
						</div>

						<a
							href="https://maps.google.com/?q=Little+Rockstar+Pre+School+Ranasinghpur+Bhubaneswar"
							target="_blank"
							rel="noopener noreferrer"
							className={styles.ViewOnMapsBtn}
						>
							<span>View on maps</span>
							<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
								<line x1="5" y1="12" x2="19" y2="12"></line>
								<polyline points="12 5 19 12 12 19"></polyline>
							</svg>
						</a>
					</div>

					{/* Right Map Card */}
					<div className={styles.MapCard}>
						<a
							href="https://maps.google.com/?q=Little+Rockstar+Pre+School+Ranasinghpur+Bhubaneswar"
							target="_blank"
							rel="noopener noreferrer"
							className={styles.MapCardLink}
						>
							<img
								src={planVisitMapImg}
								alt="Little Rockstar Pre School Location Map"
								className={styles.MapCardImage}
							/>
						</a>
					</div>
				</section>

				{/* Inquiry Form & Info Section */}
				<section
					ref={inquiryFormRef}
					id="inquiry-form"
					className={styles.InquirySection}
				>
					{/* Left Inquiry Form */}
					<div className={styles.FormCard}>
						<h2 className={styles.FormTitle}>INQUIRY FORM</h2>

						<form onSubmit={handleSubmit} className={styles.FieldsStack}>
							{/* Parent Name */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>
									<span className={styles.RequiredStar}>*</span>Parent Name
								</label>
								<input
									type="text"
									name="parentName"
									value={formData.parentName}
									onChange={handleChange}
									required
									placeholder="Enter your name"
									className={styles.Input}
								/>
							</div>

							{/* Phone Number */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>
									<span className={styles.RequiredStar}>*</span>Phone Number
								</label>
								<input
									type="tel"
									name="phoneNumber"
									value={formData.phoneNumber}
									onChange={handleChange}
									required
									placeholder="Enter 10-digit number"
									className={styles.Input}
								/>
							</div>

							{/* Email */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>Email</label>
								<input
									type="email"
									name="email"
									value={formData.email}
									onChange={handleChange}
									placeholder="Enter your email"
									className={styles.Input}
								/>
							</div>

							{/* Child Name & Child Age */}
							<div className={styles.FieldsRow}>
								<div className={styles.FieldGroup}>
									<label className={styles.FieldLabel}>
										<span className={styles.RequiredStar}>*</span>Child’s Name
									</label>
									<input
										type="text"
										name="childName"
										value={formData.childName}
										onChange={handleChange}
										required
										placeholder="Child's full name"
										className={styles.Input}
									/>
								</div>

								<div className={styles.FieldGroup}>
									<label className={styles.FieldLabel}>
										<span className={styles.RequiredStar}>*</span>Child’s Age
									</label>
									<input
										type="text"
										name="childAge"
										value={formData.childAge}
										onChange={handleChange}
										required
										placeholder="e.g. 3.5 years"
										className={styles.Input}
									/>
								</div>
							</div>

							{/* Message */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>Message</label>
								<textarea
									name="message"
									value={formData.message}
									onChange={handleChange}
									placeholder="Tell us about your requirements or questions..."
									className={styles.Textarea}
								></textarea>
							</div>

							{/* Submit Button */}
							<button type="submit" className={styles.SubmitBtn} disabled={loading}>
								{loading ? "Sending Inquiry..." : (submitted ? "Inquiry Sent!" : "Send Inquiry")}
							</button>
						</form>
					</div>

					{/* Right Info and Rockstar Band Kids Illustration */}
					<div className={styles.InquireInfo}>
						<div className={styles.InquireHeader}>
							<span className={styles.InquireSubtitle}>Have a question ?</span>
							<h2 className={styles.InquireTitle}>
								Inquire about Little Rockstar
							</h2>
							<p className={styles.InquireDescription}>
								Interested in admissions or want to know more? Get in touch with our team and we'll be happy to help.
							</p>
						</div>

						{/* 4 Feature Cards */}
						<div className={styles.FeatureCardsList}>
							{/* Card 1: Admission Form */}
							<div
								className={`${styles.FeatureCard} ${styles.CardAdmission} ${styles.ClickableCard}`}
								onClick={() => navigate("/Admission/AdmissionForm")}>
								<div className={`${styles.CardIconCircle} ${styles.PurpleIconRing}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7B61FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<path d="M22 10v6M2 10l10-5 10 5-10 5z" />
										<path d="M6 12v5c3 3 9 3 12 0v-5" />
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Admission Form</h3>
									<p>Learn about our admission process and apply online.</p>
								</div>
							</div>

							{/* Card 2: Programs and music */}
							<div
								className={`${styles.FeatureCard} ${styles.CardPrograms} ${styles.ClickableCard}`}
								onClick={scrollToGallery}>
								<div className={`${styles.CardIconCircle} ${styles.PinkIconRing}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF62A5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<path d="M9 18V5l12-2v13" />
										<circle cx="6" cy="18" r="3" />
										<circle cx="18" cy="16" r="3" />
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Programs and music</h3>
									<p>Explore our space, activities, and joyful rhythm.</p>
								</div>
							</div>

							{/* Card 3: Visit Information */}
							<div
								className={`${styles.FeatureCard} ${styles.CardVisit} ${styles.ClickableCard}`}
								onClick={scrollToVisit}>
								<div className={`${styles.CardIconCircle} ${styles.GreenIconRing}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
										<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
										<circle cx="12" cy="10" r="3" />
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Visit Information</h3>
									<p>Plan a visit and explore our joyful learning environment.</p>
								</div>
							</div>

							{/* Card 4: Call directly */}
							<div className={`${styles.FeatureCard} ${styles.DashedCallCard}`}>
								<div className={styles.SolidCallIconCircle}>
									<FaPhoneAlt size={17} color="#ffffff" />
								</div>
								<div className={styles.CardText}>
									<h3>Or call us directly at</h3>
									<div className={styles.PhoneRow}>
										<a href="tel:7848933200">78489 33200</a>
										<a href="tel:8013040248">80130 40248</a>
									</div>
								</div>
							</div>
						</div>

						{/* Rockstar Band Kids illustration with Floating Music Notes */}
						<div className={styles.RockstarIllustrationWrapper}>
							<div className={styles.BackdropGlow}></div>
							<img src={musicNotesLeftImg} alt="Music Notes" className={styles.MusicNotesLeft} />
							<img src={rockstarBandKidsImg} alt="Little Rockstar Band Kids" className={styles.RockstarKidsImg} />
							<img src={musicNotesRightImg} alt="Music Notes" className={styles.MusicNotesRight} />
						</div>
					</div>
				</section>
			</div>

			<Footer />
		</div>
	);
};

export default LittleRockstar;
